import cubejs, {
  LoadMethodOptions,
  Query,
  ResultSet,
} from '@cubejs-client/core';
import { ICubeAdapter } from './cube.adapter.interface';
import { LogManager } from '../../../common/port/log/log.manager.port';
import { QueryErrorMessages } from '../../../common/enum/cube-error-message.enum';
import { AdapterErrorException } from '../../../common';
import { CubeConnectionPool, CubeConnectionPoolConfig } from './cube-connection-pool';

const exceptionMessageFactory = (error: any): string => {
  if (error.status === undefined) {
    return QueryErrorMessages.RateLimitExceeded;
  }
  if (error.status === 403) {
    return `${QueryErrorMessages.AuthException}: ${error.response.error}`;
  }
  if (error.status === 400 || error.status === 500) {
    return `${QueryErrorMessages.ApiCallError}: ${error.response.error}`;
  }
  return QueryErrorMessages.ApiCallError;
};

export class CubeAdapter implements ICubeAdapter {
  private _requestCount: number = 0;
  private connectionPool: CubeConnectionPool;

  constructor(
    protected readonly logManager: LogManager,
    protected readonly endpoint: string,
    poolConfig?: CubeConnectionPoolConfig
  ) {
    this.connectionPool = new CubeConnectionPool(
      this.logManager,
      this.endpoint,
      poolConfig
    );
    this.logManager.debug('Client initialized with connection pooling');
  }

  /**
   * Generic cube api wrapper method. For use in Domain repositories only.
   * @param token {string} CUBE API Token
   * @param data {Cube.Query} CUBE Query object
   * @param options? {Cube.LoadMethodOptions} Optional CUBE Query method options object
   * @returns Cube api response
   */
  async query(
    token: string,
    data: Query,
    options?: LoadMethodOptions
  ): Promise<any[]> {
    this._requestCount++;
    let client;
    
    try {
      client = await this.connectionPool.getConnection(token);
      this.logManager.debug(
        'calling api with data',
        JSON.stringify(data, null, 4)
      );
      const response: ResultSet = await client.load(data, {
        ...options,
        castNumerics: false,
      });
      this.logManager.debug('response', response.rawData());
      return response.rawData();
    } catch (error) {
      console.log(JSON.stringify(error));
      this.logManager.debug('Request count at exception', this._requestCount);
      const message = exceptionMessageFactory(error);
      throw new AdapterErrorException(
        `${QueryErrorMessages.ApiCallError} ${message}`
      );
    } finally {
      if (client) {
        this.connectionPool.releaseConnection(token, client);
      }
    }
  }

  /**
   * Get connection pool statistics
   */
  getPoolStats(): Record<string, any> {
    return this.connectionPool.getStats();
  }

  /**
   * Close the connection pool
   */
  async close(): Promise<void> {
    await this.connectionPool.close();
  }
}
